import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { ExamPaperDetail, ExamSessionMode, createExamSession, fetchExamPaperDetail, fetchExamSessions, ExamSessionListItem, openExamPaperPdf } from '../../api/exam'
import { ROUTES, generatePath } from '@/constants/routes'

export default function ExamDetailPage() {
  const { paperId } = useParams<{ paperId: string }>()
  const navigate = useNavigate()
  const [paper, setPaper] = useState<ExamPaperDetail | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [starting, setStarting] = useState(false)
  const [unfinishedSession, setUnfinishedSession] = useState<ExamSessionListItem | null>(null)
  const [startMode, setStartMode] = useState<ExamSessionMode>('test')
  const [downloadingPdf, setDownloadingPdf] = useState<'plain' | 'answers' | null>(null)

  async function handleDownloadPdf(withAnswers: boolean) {
    if (!paperId) return
    try {
      setDownloadingPdf(withAnswers ? 'answers' : 'plain')
      await openExamPaperPdf(paperId, withAnswers)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setDownloadingPdf(null)
    }
  }

  useEffect(() => {
    if (!paperId) return
    let mounted = true
    setLoading(true)
    
    Promise.all([
      fetchExamPaperDetail(paperId),
      fetchExamSessions({ paperId, status: 'in_progress' })
    ])
      .then(([paperData, sessions]) => {
        if (mounted) {
          setPaper(paperData)
          if (sessions && sessions.length > 0) {
            setUnfinishedSession(sessions[0])
          }
        }
      })
      .catch((err: unknown) => {
        if (mounted) {
          setError(err instanceof Error ? err.message : String(err))
        }
      })
      .finally(() => {
        if (mounted) {
          setLoading(false)
        }
      })
    return () => {
      mounted = false
    }
  }, [paperId])

  async function handleStartExam(forceNew: boolean = false) {
    if (!paperId) return
    try {
      setStarting(true)
      // 继续未完成会话时后端会返回原会话（保持原模式），新会话使用所选模式
      const session = await createExamSession(paperId, forceNew ? startMode : (unfinishedSession?.mode as ExamSessionMode) ?? startMode, forceNew)
      navigate(generatePath(ROUTES.EXAM_SESSION, { sessionId: session.id }))
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setStarting(false)
    }
  }

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">{paper ? paper.name : '试卷详情'}</h1>
        <Link to={ROUTES.EXAM} className="text-sm text-blue-600 hover:underline">
          返回试卷列表
        </Link>
      </div>
      {loading && <div>加载中...</div>}
      {error && <div className="text-red-500 text-sm">{error}</div>}
      {!loading && !error && paper && (
        <div className="space-y-4">
          <div className="text-sm text-muted-foreground space-x-2">
            {paper.subject && <span>{paper.subject}</span>}
            {paper.exam_type && <span>{paper.exam_type}</span>}
            {paper.year && <span>{paper.year}</span>}
            {paper.stage && <span>{paper.stage}</span>}
            {paper.grade && <span>{paper.grade}</span>}
          </div>
          <div className="text-sm text-muted-foreground space-x-4">
            {typeof paper.total_score === 'number' && <span>总分：{paper.total_score}</span>}
            {paper.estimated_duration_minutes && <span>建议用时：{paper.estimated_duration_minutes} 分钟</span>}
            {paper.difficulty_level && <span>难度：{paper.difficulty_level}</span>}
          </div>
          {/* 作答模式选择 */}
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
            <div className="text-xs font-medium text-slate-500 mb-2">作答模式</div>
            <div className="flex flex-col gap-2 sm:flex-row sm:gap-3">
              {([
                {
                  value: 'practice' as ExamSessionMode,
                  label: '普通模式',
                  desc: '可逐题使用“智能讲解”和“AI 智能批改”',
                },
                {
                  value: 'test' as ExamSessionMode,
                  label: '测试模式',
                  desc: '模拟真实考试，作答中不提供讲解与批改，提交后统一批改并生成报告',
                },
                {
                  value: 'offline' as ExamSessionMode,
                  label: '线下作答',
                  desc: '下载试卷纸笔作答，完成后上传作答纸照片，AI 自动识别、批改并生成报告',
                },
              ]).map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setStartMode(opt.value)}
                  className={[
                    'flex-1 rounded-lg border px-3 py-2 text-left transition-colors',
                    startMode === opt.value
                      ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-200'
                      : 'border-slate-200 bg-white hover:border-slate-300',
                  ].join(' ')}
                >
                  <div className="text-sm font-medium text-slate-800">
                    {opt.label}
                    {startMode === opt.value && <span className="ml-2 text-xs text-blue-600">✓ 已选择</span>}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">{opt.desc}</div>
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="flex gap-4">
              {unfinishedSession ? (
                <>
                  <button
                    type="button"
                    onClick={() => navigate(generatePath(ROUTES.EXAM_SESSION, { sessionId: unfinishedSession.id }))}
                    className="inline-flex items-center px-4 py-2 rounded-md bg-green-600 text-white text-sm hover:bg-green-700"
                  >
                    继续上次练习
                  </button>
                  <button
                    type="button"
                    onClick={() => handleStartExam(true)}
                    disabled={starting}
                    className="inline-flex items-center px-4 py-2 rounded-md bg-white text-red-600 border border-red-200 text-sm hover:bg-red-50"
                  >
                    {starting ? '正在创建...' : '重新开始'}
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  onClick={() => handleStartExam(false)}
                  disabled={starting}
                  className="inline-flex items-center px-4 py-2 rounded-md bg-blue-600 text-white text-sm disabled:opacity-60"
                >
                  {starting ? '正在创建...' : '开始整套模拟考试'}
                </button>
              )}
              {/* 试卷 PDF 下载（线下作答用） */}
              <button
                type="button"
                onClick={() => handleDownloadPdf(false)}
                disabled={downloadingPdf !== null}
                className="inline-flex items-center px-4 py-2 rounded-md bg-white text-slate-700 border border-slate-300 text-sm hover:bg-slate-50 disabled:opacity-60"
              >
                {downloadingPdf === 'plain' ? '正在生成...' : '下载试卷 PDF'}
              </button>
              <button
                type="button"
                onClick={() => handleDownloadPdf(true)}
                disabled={downloadingPdf !== null}
                className="inline-flex items-center px-4 py-2 rounded-md bg-white text-slate-500 border border-slate-200 text-sm hover:bg-slate-50 disabled:opacity-60"
              >
                {downloadingPdf === 'answers' ? '正在生成...' : '含答案解析版'}
              </button>
            </div>
          </div>
          <div className="border rounded-md">
            <div className="border-b px-4 py-2 text-sm font-medium bg-muted">题目列表</div>
            <div className="divide-y">
              {paper.questions.map((q) => (
                <div key={q.question_id} className="px-4 py-2 text-sm flex items-start justify-between gap-4">
                  <div>
                    <div className="font-medium">
                      第 {q.index_in_paper} 题 {q.section && <span className="text-xs text-muted-foreground">({q.section})</span>}
                    </div>
                    <div className="text-xs text-muted-foreground space-x-2 mt-1">
                      {q.type && <span>类型：{q.type}</span>}
                      {typeof q.difficulty === 'number' && <span>难度：{q.difficulty}</span>}
                    </div>
                    {q.knowledge_points && q.knowledge_points.length > 0 && (
                      <div className="text-xs text-muted-foreground mt-1">
                        知识点：{q.knowledge_points.join('，')}
                      </div>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground text-right">
                    {typeof q.score === 'number' && <div>分值：{q.score}</div>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
